'use client'

import { useRef, useState } from 'react'
import { Play, Volume2, VolumeX } from 'lucide-react'

/**
 * Sample gallery for /models/seedance-2-5.
 *
 * This page used to render `SeedanceMiniGallery` — literally the same component
 * and the same clips as /models/seedance-2-mini — so the page selling Seedance
 * 2.5 was illustrated entirely with a different, cheaper model's output, and
 * three of those clips were hotlinked straight off ByteDance's CDN.
 *
 * Every clip below was generated on `bytedance/seedance-2.5/text-to-video`,
 * which is the endpoint this page's own studio submits to, at the 720p the
 * studio defaults to. The prompt that produced each one ships with it: on a
 * page whose job is to sell a text-to-video model, the prompt is half the
 * evidence, and a gallery that hides it is asking to be taken on faith.
 */

const CDN = 'https://cdn.soku.ai/marketing/videos/tools/seedance-2-5'

type Sample = {
  slug: string
  label: string
  /** The exact prompt submitted to 2.5, so the result is checkable. */
  prompt: string
  tag: string
  /** Tailwind aspect class — 2.5 honours the ratio it is asked for. */
  aspect: string
  audio: boolean
}

const samples: Sample[] = [
  {
    slug: 'a',
    label: 'Forge strike',
    prompt:
      'A blacksmith hammers glowing orange steel on an anvil in a dim workshop, sparks bursting on each strike, smoke drifting through a shaft of window light.',
    tag: 'Native audio',
    aspect: 'aspect-video',
    audio: true,
  },
  {
    slug: 'c',
    label: 'Surf run',
    prompt:
      'A golden retriever bounds through shallow surf at sunset, water spraying from each stride, low camera tracking alongside at wave height.',
    tag: 'Motion',
    aspect: 'aspect-video',
    audio: true,
  },
  {
    slug: 'f',
    label: 'Chocolate pour',
    prompt:
      'Molten chocolate pours over a glossy cake in slow motion, folding and settling in thick ribbons, warm studio light raking across the surface.',
    tag: 'Macro',
    aspect: 'aspect-video',
    audio: true,
  },
  {
    slug: 'b',
    label: 'Espresso pull',
    prompt:
      'Close-up of a barista pulling an espresso shot: dark crema streams into a warm ceramic cup, steam curling, morning light across the brushed steel.',
    tag: 'Vertical',
    aspect: 'aspect-[9/16]',
    audio: true,
  },
  {
    slug: 'e',
    label: 'Neon alley',
    prompt:
      'A woman in a yellow raincoat walks a neon-lit Tokyo alley at night, rain streaking through the signs, reflections rippling in puddles as she passes.',
    tag: 'Vertical',
    aspect: 'aspect-[9/16]',
    audio: true,
  },
  {
    slug: 'd',
    label: 'Product turn',
    prompt:
      'A single running shoe on a pale concrete plinth in a bright studio, rotating slowly to face camera. Soft daylight from the left, clean white cyclorama behind.',
    tag: 'Square',
    aspect: 'aspect-square',
    audio: false,
  },
]

export function Seedance25Gallery() {
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])
  const [soundOn, setSoundOn] = useState<number | null>(null)

  const handleEnter = (i: number) => {
    const v = videoRefs.current[i]
    if (v) void v.play().catch(() => {})
  }

  const handleLeave = (i: number) => {
    const v = videoRefs.current[i]
    if (!v) return
    v.pause()
    v.currentTime = 0
  }

  /** One clip audible at a time — six autoplaying soundtracks is a bounce. */
  const toggleSound = (i: number) => {
    const next = soundOn === i ? null : i
    setSoundOn(next)
    videoRefs.current.forEach((v, idx) => {
      if (!v) return
      v.muted = idx !== next
      if (idx === next) void v.play().catch(() => {})
    })
  }

  return (
    <div className="mt-8 gap-5 sm:columns-2 lg:columns-3 [column-fill:_balance]">
      {samples.map((s, i) => (
        <div
          key={s.slug}
          className="group border-border bg-card mb-5 inline-block w-full break-inside-avoid overflow-hidden rounded-2xl border shadow-sm transition-shadow hover:shadow-md"
          onMouseEnter={() => handleEnter(i)}
          onMouseLeave={() => handleLeave(i)}
        >
          <div className={`relative overflow-hidden bg-black ${s.aspect}`}>
            <video
              ref={el => {
                videoRefs.current[i] = el
              }}
              src={`${CDN}/sample-${s.slug}.mp4`}
              poster={`${CDN}/poster-${s.slug}.jpg`}
              muted
              loop
              playsInline
              preload="none"
              className="h-full w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/15 opacity-100 transition-opacity group-hover:opacity-0">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-1.5 text-xs font-medium text-white">
                <Play className="size-3.5" /> Hover to play
              </span>
            </div>
            <span className="absolute top-3 left-3 rounded-full bg-black/55 px-2.5 py-1 text-[10px] font-semibold tracking-wider text-white uppercase">
              {s.tag}
            </span>
            {s.audio ? (
              <button
                type="button"
                aria-label={soundOn === i ? 'Mute' : 'Unmute'}
                className="absolute right-3 bottom-3 rounded-full bg-black/55 p-1.5 text-white/90 transition-colors hover:bg-black/75"
                onClick={() => toggleSound(i)}
              >
                {soundOn === i ? (
                  <Volume2 className="size-4" />
                ) : (
                  <VolumeX className="size-4" />
                )}
              </button>
            ) : null}
          </div>
          <div className="p-4">
            <h3 className="text-foreground text-sm font-semibold">{s.label}</h3>
            <p className="text-muted-foreground mt-1 text-xs leading-5">
              &ldquo;{s.prompt}&rdquo;
            </p>
          </div>
        </div>
      ))}
    </div>
  )
}
